import { useCallback, useEffect, useRef } from 'react'
import { getMmcInstance } from '@common/oneclub/utils/getMmcInstance'

interface IOptions {
  /** ID 元素ID */
  id: string
  /** 可以指定observer监听的容器节点ID，缩小监听范围 */
  observerTargetId?: string
  /** mutation observe的配置 */
  observeOptions?: MutationObserverInit
  /** 自定义mutation的判断 */
  customMutationRecordHandle?: (record: MutationRecord) => boolean
  /** 多场景时，动态向下传递publicKey */
  publicKey?: string
}

type TMmcInstance = ReturnType<
  (params: { env: string; publicKey: string; timeout?: number }) => {
    listen: (
      event: string,
      cb: (token: string) => void
    ) => {
      destroy?: () => void
    }
  }
>
const DEFAULT_MAX_INTERVAL_TIME = 7000

const useMmcToken = (options: IOptions) => {
  const { id, observerTargetId, observeOptions, customMutationRecordHandle, publicKey } = options
  const observer = useRef<MutationObserver>()
  const mmcInstance = useRef<TMmcInstance>()
  const isRegistered = useRef<boolean>(false)
  const currentToken = useRef<string>()
  const listenerResolve = useRef<(value?: string) => void>()
  const timer = useRef<number>()
  const listener = useRef<ReturnType<TMmcInstance['listen']>>()

  const setMmcListener = useCallback((targetId: string) => {
    if (!mmcInstance.current || isRegistered.current) {
      return
    }

    listener.current = mmcInstance.current.listen(targetId, (token?: string) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
      currentToken.current = token
      listenerResolve.current?.(token)
    })
    isRegistered.current = true
  }, [])

  const setMutationObserver = useCallback(() => {
    if (observer.current) {
      observer.current.disconnect()
    }
    observer.current = new MutationObserver((mutationList) => {
      mutationList.forEach((mutation) => {
        if (customMutationRecordHandle?.(mutation) ?? mutation.type === 'childList') {
          if (document.getElementById(id)) {
            setMmcListener(id)
          } else {
            if (listener.current) {
              listener.current.destroy?.()
              isRegistered.current = false
            }
          }
        }
      })
    })

    const target = observerTargetId ? document.getElementById(observerTargetId) : document.body
    if (target) {
      observer.current.observe(
        target,
        observeOptions ?? {
          subtree: true,
          childList: true,
        }
      )
    }
  }, [customMutationRecordHandle, id, observeOptions, observerTargetId, setMmcListener])

  const initMmc = useCallback(async () => {
    if (id) {
      mmcInstance.current = await getMmcInstance(publicKey)
      if (mmcInstance.current) {
        if (document.getElementById(id)) {
          setMmcListener(id)
        } else {
          setMutationObserver()
        }
      }
    }
  }, [id, setMmcListener, setMutationObserver, publicKey])

  useEffect(() => {
    initMmc()
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
      if (listener.current?.destroy) {
        listener.current.destroy()
      }
    }
  }, [initMmc])

  const getMToken = async (): Promise<string | undefined> => {
    if (currentToken.current) {
      const token = currentToken.current
      currentToken.current = undefined
      return token
    }

    const token = await new Promise<string | undefined>((resolve) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
      listenerResolve.current = resolve
      timer.current = window.setTimeout(() => resolve(''), DEFAULT_MAX_INTERVAL_TIME)
    })

    currentToken.current = undefined

    return token
  }

  return {
    getMToken,
  }
}

export default useMmcToken
